import React from 'react';

/**
 * The bundled polished button component (Rule 56). Every common action button
 * in this kit — Save / Create / Delete / Cancel / Export / Retry / etc., in
 * tables, modals, forms, and page toolbars — uses `<Button variant="...">`,
 * NOT hand-rolled `<button class="rounded bg-blue-500">` markup.
 *
 * Sidebar nav and the topbar toolbar buttons (`.shell-btn-*`) are *not* in
 * scope and intentionally keep their own classes — they're chrome, not
 * actions.
 *
 * Variants — one of:
 *   primary | secondary | success | danger | warning | light | dark | link
 *
 * Sizes — one of:
 *   sm | md (default) | lg
 *
 * Polish — each non-link variant ships a vertical gradient (lighter top →
 * deeper bottom), a 1px outer border that's slightly darker than the bottom
 * stop, a 1px-inset top highlight (`.btn::before` in index.css) for the
 * "wet plastic" gloss, and a soft outer drop-shadow. Hover brightens; active
 * inverts the highlight + nudges 1px down (the classic Aqua press).
 *
 * Adding a new variant = a new entry in `index.css`'s `.btn-{name}` block;
 * NEVER inline-style or bypass the system.
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  type = 'button',
  leftIcon,
  rightIcon,
  loading = false,
  disabled,
  className = '',
  children,
  ...rest
}) {
  const classes = [
    'btn',
    `btn-${variant}`,
    `btn-${size}`,
    loading ? 'is-loading' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="btn-spinner" aria-hidden="true" /> : leftIcon}
      <span className="btn-label">{children}</span>
      {!loading && rightIcon}
    </button>
  );
}
